import { TextField, Typography } from "@mui/material";
import { useOrganizationContext } from "context/OrganisationContext";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import * as authApi from "./helpers/authApi";
import { getErrorMessage } from "./helpers/authHelpers";
import { InputGroup, LoginButton, LoginCard, LoginContainer } from "./styles";

export interface Props {}

interface ResetPasswordFormValues {
	password: string;
	confirmPassword: string;
}

const ResetPasswordConfirmScreen: React.FC<Props> = (props: Props) => {
	const org = useOrganizationContext();
	const intl = useIntl();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = React.useState(false);
	const [showSuccessCard, setShowSuccessCard] = React.useState(false);
	const [error, setError] = React.useState<any>(null);

	const searchParams = new URLSearchParams(window.location.search);
	const tokenRef = React.useRef(searchParams.get("token"));
	const submit = async (val: ResetPasswordFormValues) => {
		setIsLoading(true);
		setError("");
		try {
			await authApi.resetPasswordConfirm(tokenRef.current!, val.password);
			setShowSuccessCard(true);
		} catch (err) {
			const message = getErrorMessage((err as any).json.message);

			setError(message);
		} finally {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		if (!tokenRef.current && !org.loading) {
			navigate(`/${org.value.slug}`, { replace: true });
		}
	}, [navigate, org.value, org.loading]);

	if (org.loading) {
		return null;
	}
	return (
		<AuthLayout>
			<Helmet>
				<title>{intl.formatMessage({ id: "RESET_PASSWORD.page.confirm_title" })}</title>
			</Helmet>
			<LoginContainer>
				<LoginCard>
					{showSuccessCard ? (
						<div>
							<Typography
								variant="h5"
								style={{ fontWeight: 800, fontSize: "32px", margin: 0, marginBottom: 16 }}
							>
								{intl.formatMessage({ id: "RESET_PASSWORD.success-confirm-form.title" })}
							</Typography>
							<Typography variant="caption" style={{ fontWeight: "normal", fontSize: "18px" }}>
								{/* @ts-ignore */}
								{intl.formatMessage(
									{ id: "RESET_PASSWORD.success-confirm-form.description" },
									{ a: (text) => <a href={`/${org.value.slug}`}>{text}</a> },
								)}
							</Typography>
						</div>
					) : (
						<ResetPasswordForm isLoading={isLoading} submit={submit} />
					)}
					{error}
				</LoginCard>
			</LoginContainer>
		</AuthLayout>
	);
};

const ResetPasswordForm = ({ isLoading, submit }) => {
	const { register, handleSubmit, errors, watch } = useForm<ResetPasswordFormValues>();
	const password = watch("password", "");
	const intl = useIntl();

	return (
		<>
			<div style={{ marginBottom: 16 }}>
				<Typography variant="h5" style={{ fontWeight: 800, fontSize: "32px", margin: 0, marginBottom: 16 }}>
					{intl.formatMessage({
						id: "AUTH.reset-password-confirm-screen.reset_your_password",
					})}
				</Typography>
				<Typography variant="caption" style={{ fontWeight: "normal", fontSize: "18px" }}>
					{intl.formatMessage({
						id: "AUTH.reset-password-confirm-screen.please_enter_new",
					})}
				</Typography>
			</div>
			<form onSubmit={handleSubmit(submit)}>
				<div style={{ display: "flex", flexDirection: "column" }}>
					<InputGroup>
						<TextField
							id="field-password"
							inputRef={register({ required: true })}
							variant="outlined"
							fullWidth
							type="password"
							name="password"
							label={intl.formatMessage({ id: "AUTH.login.input.password.label" })}
							autoComplete="off"
							disabled={isLoading}
							helperText={errors.password?.message}
							size="small"
							error={!!errors.password?.message}
							sx={{
								"& .MuiFormLabel-root": {
									fontSize: "18px",
								},
								"& .MuiInputBase-root": {
									fontWeight: 500,
									fontSize: "18px",
									borderRadius: "8px",
								},
							}}
						/>
					</InputGroup>
					<InputGroup>
						<TextField
							id="field-confirm-password"
							inputRef={register({
								required: true,
								validate: (value) => value === password || "The passwords do not match",
							})}
							variant="outlined"
							fullWidth
							type="password"
							name="confirmPassword"
							label={intl.formatMessage({ id: "AUTH.reset-password-confirm.confirm-password.label" })}
							autoComplete="off"
							disabled={isLoading}
							helperText={errors.confirmPassword?.message}
							size="small"
							error={!!errors.confirmPassword?.message}
							sx={{
								"& .MuiFormLabel-root": {
									fontSize: "18px",
								},
								"& .MuiInputBase-root": {
									fontWeight: 500,
									fontSize: "18px",
									borderRadius: "8px",
								},
							}}
						/>
					</InputGroup>
					<div style={{ margin: "8px 0px" }} />
					<InputGroup>
						<LoginButton
							type="submit"
							variant="contained"
							color="primary"
							fullWidth
							disableElevation
							disabled={isLoading}
						>
							{intl.formatMessage({
								id: "AUTH.reset-password-confirm-screen.change_password",
							})}
						</LoginButton>
					</InputGroup>
				</div>
			</form>
		</>
	);
};

export default ResetPasswordConfirmScreen;
